/**
 * Store del bilancio Camposcuola: Context + useReducer, zero dipendenze extra.
 *
 * Principio: nello stato vivono SOLO gli input (impostazioni evento + partecipanti).
 * Il bilancio (GAP, quota base, righe, anomalie) è sempre derivato con useMemo da
 * computeBilancio(): qualunque dispatch — anche solo cambiare le notti_effettive di
 * un singolo partecipante — fa ripartire l'intero algoritmo. Non può andare fuori sync.
 *
 * Uso:
 *   <CamposcuolaBilancioProvider>...</CamposcuolaBilancioProvider>
 *
 *   const { impostazioni, partecipanti, bilancio, actions } = useCamposcuolaBilancio();
 *   actions.patchPartecipante(id, { notti_effettive: 2 });      // → GAP ricalcolato
 *   actions.patchPartecipante(id, { override_quota: 30, motivo_override: "Quota staff" });
 *   actions.setImpostazioni({ override_globale_quota_base: 190 }); // → quota tonda
 */
import { createContext, useContext, useMemo, useReducer, useCallback } from "react";
import { computeBilancio } from "@/lib/camposcuolaEngine";

/** @typedef {import("@/lib/camposcuolaEngine").ImpostazioniEvento} ImpostazioniEvento */
/** @typedef {import("@/lib/camposcuolaEngine").Partecipante} Partecipante */

export const IMPOSTAZIONI_DEFAULT = {
  costo_notte_struttura: 0,
  notti_totali_evento: 1,
  override_globale_quota_base: null,
  voci_spese_extra: [], // voci libere di costo {id, descrizione, importo}
  voci_entrate: [],     // voci libere d'entrata {id, descrizione, importo}
};

export const PARTECIPANTE_DEFAULT = {
  ruolo: "animatore",
  notti_effettive: null, // null → eredita notti_totali_evento
  override_quota: null,  // null → segue la quota base (se animatore/ospite)
  motivo_override: "",
  importo_versato: 0,
};

const initialState = {
  impostazioni: IMPOSTAZIONI_DEFAULT,
  partecipanti: /** @type {Partecipante[]} */ ([]),
};

function reducer(state, action) {
  switch (action.type) {
    case "HYDRATE":
      // Carica tutto insieme (es. dalla GET /camposcuola)
      return {
        impostazioni: { ...IMPOSTAZIONI_DEFAULT, ...action.impostazioni },
        partecipanti: (action.partecipanti || []).map((p) => ({ ...PARTECIPANTE_DEFAULT, ...p })),
      };

    case "SET_IMPOSTAZIONI":
      // Patch parziale: setImpostazioni({ override_globale_quota_base: 190 })
      return { ...state, impostazioni: { ...state.impostazioni, ...action.patch } };

    case "UPSERT_PARTECIPANTE": {
      const nuovo = { ...PARTECIPANTE_DEFAULT, ...action.partecipante };
      const esiste = state.partecipanti.some((p) => p.id === nuovo.id);
      return {
        ...state,
        partecipanti: esiste
          ? state.partecipanti.map((p) => (p.id === nuovo.id ? { ...p, ...nuovo } : p))
          : [...state.partecipanti, nuovo],
      };
    }

    case "PATCH_PARTECIPANTE":
      // Patch parziale del singolo: notti_effettive, override_quota, importo_versato...
      return {
        ...state,
        partecipanti: state.partecipanti.map((p) =>
          p.id === action.id ? { ...p, ...action.patch } : p
        ),
      };

    case "REMOVE_PARTECIPANTE":
      return { ...state, partecipanti: state.partecipanti.filter((p) => p.id !== action.id) };

    default:
      return state;
  }
}

const Ctx = createContext(null);

export function CamposcuolaBilancioProvider({ children, initial }) {
  const [state, dispatch] = useReducer(
    reducer,
    initial ? reducer(initialState, { type: "HYDRATE", ...initial }) : initialState
  );

  // Il cuore reattivo: qualunque modifica allo stato → bilancio ricalcolato.
  const bilancio = useMemo(
    () => computeBilancio(state.partecipanti, state.impostazioni),
    [state.partecipanti, state.impostazioni]
  );

  const actions = useMemo(
    () => ({
      hydrate: (impostazioni, partecipanti) => dispatch({ type: "HYDRATE", impostazioni, partecipanti }),
      setImpostazioni: (patch) => dispatch({ type: "SET_IMPOSTAZIONI", patch }),
      upsertPartecipante: (partecipante) => dispatch({ type: "UPSERT_PARTECIPANTE", partecipante }),
      patchPartecipante: (id, patch) => dispatch({ type: "PATCH_PARTECIPANTE", id, patch }),
      removePartecipante: (id) => dispatch({ type: "REMOVE_PARTECIPANTE", id }),
    }),
    []
  );

  const value = useMemo(
    () => ({ ...state, bilancio, actions }),
    [state, bilancio, actions]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCamposcuolaBilancio() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCamposcuolaBilancio va usato dentro <CamposcuolaBilancioProvider>");
  return ctx;
}

/**
 * Comodo per la futura UI di bilancio: selettore già pronto.
 * const gap = useGapCamposcuola(); // numero, si aggiorna in tempo reale
 */
export function useGapCamposcuola() {
  return useCamposcuolaBilancio().bilancio.gap;
}
